import type { AxiosPromise } from 'axios';

import type { FilterObjectData } from '@app/components/CustomFilter';

import request from '../../../../utils/request';
import type { Contributor, EditContributors, GetContributors, GetTasks, PutTasks } from './types';

type GetQueriedTasksCallParams = {
    projectId: string;
    query: string;
    branch: string;
    page?: number;
    pageSize?: number;
    search?: string;
    filters?: FilterObjectData[];
};

export const getQueriedTasksCall = ({
    projectId,
    query,
    branch,
    page = 1,
    pageSize = 5,
    search = '',
    filters = [],
}: GetQueriedTasksCallParams): AxiosPromise<GetTasks> => {
    const processedFilters = filters.map((filter) => JSON.stringify(filter));

    return request({
        method: 'GET',
        url: `/tasks/${projectId}/list`,
        params: {
            status: query,
            page,
            branch,
            pageSize,
            search,
            filters: processedFilters,
        },
    });
};

export const getTaskTagCall = (projectId: string): AxiosPromise<GetTasks> =>
    request({
        method: 'GET',
        url: `/project/${projectId}/tags?_=${Date.now()}`,
    });

export const putTaskCall = (data: PutTasks): AxiosPromise =>
    request({
        method: 'PATCH',
        url: `/tasks/${data.taskId}`,
        data,
    });

export const closeTaskCall = (taskId: number): AxiosPromise =>
    request({
        method: 'POST',
        url: `/tasks/${taskId}/close`,
    });

export const closeLanguageCall = (taskId: number, langId: number): AxiosPromise =>
    request({
        method: 'POST',
        url: `/tasks/close-language`,
        data: {
            taskId,
            langId,
        },
    });

export const deleteTaskCall = (taskId: number): AxiosPromise =>
    request({
        method: 'POST',
        url: `/tasks/${taskId}/delete`,
    });

export const getInitialReportCall = (taskId: number): AxiosPromise<Blob> =>
    request({
        method: 'GET',
        url: `/tasks/${taskId}/reports/initial`,
        responseType: 'blob',
    });

export const getFinalReportCall = (taskId: number): AxiosPromise<Blob> =>
    request({
        method: 'GET',
        url: `/tasks/${taskId}/reports/final`,
        responseType: 'blob',
    });

export const getDetailedReportCall = (taskId: number): AxiosPromise<Blob> =>
    request({
        method: 'GET',
        url: `/tasks/${taskId}/report`,
        responseType: 'blob',
    });

export const getOfflineXLIFFCall = (taskId: number): AxiosPromise<Blob> =>
    request({
        method: 'GET',
        url: `/tasks/${taskId}/download-xliff`,
    });

export const postOfflineXLIFFCall = (taskId: number, files: FileList): AxiosPromise<GetTasks> => {
    const formData = new FormData();

    Array.from(files).forEach((file) => {
        formData.append('files[]', file, file.name);
    });

    return request({
        method: 'POST',
        url: `/tasks/${taskId}/upload-xliff`,
        data: formData,
    });
};

export const getContributorsCall = (payload: GetContributors): AxiosPromise<Contributor[]> =>
    request({
        method: 'GET',
        url: `/tasks/contributors-for-languages`,
        params: payload,
    });

export const editContributorsCall = (payload: EditContributors): AxiosPromise<Contributor[]> => {
    const { contributors, langId, taskId } = payload;

    return request({
        method: 'POST',
        url: `/tasks/${taskId}/save-lang-contributors`,
        data: {
            langId,
            contributors,
        },
    });
};

export const getFilterOptionsCall = (projectId: string): AxiosPromise<Contributor[]> =>
    request({
        method: 'GET',
        url: `/tasks/${projectId}/filter-data`,
    });

export const getLqaReportCall = (taskId: number): AxiosPromise<Blob> =>
    request({
        method: 'GET',
        url: `/tasks/${taskId}/reports/lqa`,
        responseType: 'blob',
    });
